import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastMessageType, ToastService } from '@app/Common';
import { EnvironmentService, EnvironmentViewModel } from '@app/Environments';
import { CreateTenantDto } from '@app/Models/create-tenant.dto';
import { TenantGroup } from '@app/Models/tenant-group.model';
import { TenantProperty } from '@app/Models/tenantProperty';
import { TenantService, TenantFormOptionsService } from '@app/Services';
import { TenantValidator } from '@app/Tenant/Validators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-new-tenant-form',
    templateUrl: './new-tenant-form.component.html',
    styleUrls: ['./new-tenant-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, NgClass, MatInput, MatError, MatIcon, MatSelect, MatOption, MatButton]
})
export class NewTenantFormComponent implements OnInit {
  @Output() formSubmitted = new EventEmitter<CreateTenantDto>();
  @Input() actionName = 'Save';
  @Input() isSubmitted: boolean;
  @Input() hasEnvironmentField = false;
  @Input() isExternalConditionValid = true;

  tenantTypes: TenantProperty[] = [];
  tenantPermissionGroups: TenantGroup[] = [];
  environments: EnvironmentViewModel[] = [];
  tenantIndustryTypes: string[] = [];
  tenantSizes: string[] = [];

  newTenantForm = new UntypedFormGroup({
    tenantSlug: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(3),
      Validators.pattern('^[a-zA-Z0-9]*$')
    ], [this.tenantValidator.getValidator()]),
    tenantName: new UntypedFormControl(null, [Validators.required]),
    tenantIndustry: new UntypedFormControl(null, [Validators.required, Validators.maxLength(100)]),
    tenantCompanySize: new UntypedFormControl(null, [Validators.required, Validators.maxLength(100)]),
    tenantType: new UntypedFormControl(null, [Validators.required]),
    permissionGroup: new UntypedFormControl(null, [Validators.required]),
    environmentId: new UntypedFormControl(null, []),
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly tenantService: TenantService,
    private readonly toast: ToastService,
    private readonly tenantFormOptionsService: TenantFormOptionsService,
    private readonly environementService: EnvironmentService,
    private readonly tenantValidator: TenantValidator
  ) { }

  ngOnInit(): void {
    this.getTenantTypes();
    this.tenantPermissionGroups = this.tenantFormOptionsService.getTenantPermissionGroups();
    this.fetchEnvironments();
    this.getTenantAttributes();
  }

  isFieldValid(formControlName: string): boolean {
    const formControl = this.newTenantForm.get(formControlName);
    return formControl.dirty && formControl.valid;
  }

  isFieldInvalid(formControlName: string): boolean {
    const formControl = this.newTenantForm.get(formControlName);
    return formControl.dirty && formControl.invalid;
  }

  hasRequiredError(formControlName: string) {
    return this.newTenantForm.get(formControlName).hasError('required');
  }

  hasMinLengthError(formControlName: string) {
    return this.newTenantForm.get(formControlName).hasError('minlength');
  }

  hasMaxLengthError(formControlName: string) {
    return this.newTenantForm.get(formControlName).hasError('maxlength');
  }

  hasPatternError(formControlName: string): boolean {
    return this.newTenantForm.get(formControlName).hasError('pattern');
  }

  tenantSlugAlreadyExists(): boolean {
    return this.newTenantForm.get('tenantSlug').hasError('tenantAlreadyExists');
  }

  isSubmitButtonAvailable(): boolean {
    return !this.isSubmitted && this.newTenantForm?.valid && this.isExternalConditionValid;
  }

  submitForm(): void {
    const newTenant = this.createTenantObject();
    this.formSubmitted.emit(newTenant);
  }

  private fetchEnvironments(): void {
    this.environementService
      .getEnvironments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (envs) => {
          this.environments = envs.filter((e) => e.AirswebCompatible);
          this.setDefaultEnvironment();
        }
      });
  }

  private setDefaultEnvironment(): void {
    if (this.environments.length === 0) {
      return;
    }
    this.newTenantForm.get('environmentId')?.setValue(this.environments[0]?.Id);
  }

  private createTenantObject(): CreateTenantDto {
    return {
      Value: this.newTenantForm.get('tenantSlug').value,
      Text: this.newTenantForm.get('tenantName').value,
      LicenceId: this.newTenantForm.get('tenantType').value,
      TenantPermissionGroup: this.newTenantForm.get('permissionGroup').value,
      EnvironmentId: this.newTenantForm.get('environmentId').value,
      Industry: this.newTenantForm.get('tenantIndustry').value,
      Size: this.newTenantForm.get('tenantCompanySize').value
    } as CreateTenantDto;
  }

  private getTenantTypes(): void {
    this.tenantService.getTenantTypes()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
            next: (types) => {
                this.tenantTypes = types;
                const tenantTypeControl = this.newTenantForm.get('tenantType');
                const enterprise = types.find((t) => t.Text === 'Enterprise');
                if (tenantTypeControl && enterprise && !tenantTypeControl.value) {
                    tenantTypeControl.setValue(enterprise.Value);
                }
            },
            error: (err) => {
                this.toast.sendMessage(
                    'Unable to get Tenant Types',
                    err,
                    ToastMessageType.error);
            }
        });
  }

  private getTenantAttributes(): void {
    this.tenantFormOptionsService.getTenantAttributes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tenantIndustryTypes, tenantSizes }) => {
          this.tenantIndustryTypes = tenantIndustryTypes;
          this.tenantSizes = tenantSizes;
        },
        error: (err) => {
          this.toast.sendMessage(
            'Unable to get Tenant Attributes',
            err,
            ToastMessageType.error);
        }
      });
  }
}
